import { useT } from '../../context/TenantContext';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import MIcon from '../../components/common/MIcon';
import ReportsTab from './ReportsTab';
import { Button } from '../../components/ui';

const ACCENT    = '#0085CA';
const ACCENT_BG = '#EBF6FC';
const BORDER    = '#E5E7EB';
const NAVY      = '#003D6B';

const TAB_ROOMS       = 'rooms';
const TAB_CATEGORIES  = 'categories';
const TAB_LOCATIONS   = 'locations';
const TAB_BOOKINGS    = 'bookings';
const TAB_REPORTS     = 'reports';

function token() { return localStorage.getItem('hd_token'); }

async function apiFetch(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const t = token();
  if (t) headers['Authorization'] = `Bearer ${t}`;
  const res = await fetch(`/api/rooms${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  return res.status === 204 ? null : res.json();
}

/* ─── Helpers UI ─── */
const inputStyle = {
  width: '100%', padding: '8px 10px', border: `1px solid ${BORDER}`, borderRadius: 7,
  fontSize: 13, boxSizing: 'border-box', outline: 'none',
};
const textareaStyle = { ...inputStyle, resize: 'vertical', minHeight: 72 };

function ErrBanner({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ fontSize: 12, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
      {msg}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, footer, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: wide ? 560 : 460, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,.15)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9CA3AF', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:   { label: 'Pendiente',  bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
    confirmed: { label: 'Confirmada', bg: '#F0FDF4', color: '#15803D', border: '#86EFAC' },
    rejected:  { label: 'Rechazada',  bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    cancelled: { label: 'Cancelada',  bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB' },
  };
  const s = map[status] || map.cancelled;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
      background: active ? '#F0FDF4' : '#F9FAFB',
      color: active ? '#15803D' : '#6B7280',
      border: `1px solid ${active ? '#86EFAC' : '#E5E7EB'}`,
    }}>
      {active ? 'Activa' : 'Inactiva'}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 1 — SALAS
══════════════════════════════════════════════════════════ */
function RoomModal({ initial, categories, locations, onSave, onClose }) {
  const [form, setForm] = useState({
    name:              initial?.name              || '',
    category_id:       initial?.category_id       || '',
    location_id:       initial?.location_id != null ? String(initial.location_id) : '',
    capacity:          initial?.capacity          ?? 1,
    description:       initial?.description       || '',
    equipment:         (() => {
      const eq = initial?.equipment;
      if (!eq) return '';
      try { const parsed = JSON.parse(eq); return Array.isArray(parsed) ? parsed.join(', ') : eq; }
      catch { return eq; }
    })(),
    requires_approval: initial?.requires_approval ? true : false,
    active:            initial?.active !== undefined ? Boolean(initial.active) : true,
  });
  const [err, setErr]     = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Editar sala' : 'Nueva sala'} onClose={onClose} wide
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : initial ? 'Guardar' : 'Crear sala'}
        </Button>
      </>}>
      <Field label="Nombre *">
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ej: Sala de reuniones A" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Categoría">
          <select style={inputStyle} value={form.category_id} onChange={e => set('category_id', e.target.value ? Number(e.target.value) : '')}>
            <option value="">Sin categoría</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Capacidad">
          <input style={inputStyle} type="number" min={1} value={form.capacity} onChange={e => set('capacity', Number(e.target.value))} />
        </Field>
      </div>
      <Field label="Ubicación">
        <select style={inputStyle} value={form.location_id} onChange={e => set('location_id', e.target.value)}>
          <option value="">Sin ubicación</option>
          {locations.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
        </select>
      </Field>
      <Field label="Descripción">
        <textarea style={textareaStyle} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Descripción opcional de la sala" />
      </Field>
      <Field label="Equipamiento (separado por comas)">
        <input style={inputStyle} value={form.equipment} onChange={e => set('equipment', e.target.value)} placeholder="Ej: proyector, pizarra, videoconferencia" />
      </Field>
      <div style={{ display: 'flex', gap: 24, marginBottom: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.requires_approval} onChange={e => set('requires_approval', e.target.checked)} />
          Requiere aprobación
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
          Sala activa
        </label>
      </div>
      <ErrBanner msg={err} />
    </Modal>
  );
}

function TabRooms({ rooms, categories, locations, onReload }) {
  const [modal, setModal] = useState(null); // null | 'new' | room object

  const handleSave = async (form) => {
    if (modal === 'new') {
      await apiFetch('/admin/rooms', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/admin/rooms/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    onReload();
  };

  const handleDelete = async (room) => {
    if (!confirm(`¿Eliminar la sala "${room.name}"? También se eliminarán todas sus reservas.`)) return;
    try {
      await apiFetch(`/admin/rooms/${room.id}`, { method: 'DELETE' });
      onReload();
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      {modal !== null && (
        <RoomModal
          initial={modal === 'new' ? null : modal}
          categories={categories}
          locations={locations}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button variant="primary" onClick={() => setModal('new')}>
          <MIcon name="add" size={16} /> Nueva sala
        </Button>
      </div>

      {rooms.length === 0 ? (
        <EmptyState icon="meeting_room" text="No hay salas todavía. Crea la primera." />
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: `1px solid ${BORDER}` }}>
                {['Nombre', 'Categoría', 'Capacidad', 'Ubicación', 'Estado', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rooms.map((room, i) => (
                <tr key={room.id} style={{ borderBottom: i < rooms.length - 1 ? `1px solid ${BORDER}` : 'none', background: '#fff' }}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{room.name}</div>
                    {room.requires_approval === 1 && (
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Requiere aprobación</div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {room.category_name ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: room.category_color || ACCENT, display: 'inline-block' }} />
                        {room.category_name}
                      </span>
                    ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{room.capacity}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {room.location_name ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: room.location_color || '#6B7280', display: 'inline-block' }} />
                        {room.location_name}
                      </span>
                    ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px' }}><ActiveBadge active={room.active} /></td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button variant="ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setModal(room)}>
                        <MIcon name="edit" size={14} /> Editar
                      </Button>
                      <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDelete(room)}>
                        <MIcon name="delete" size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 2 — CATEGORÍAS
══════════════════════════════════════════════════════════ */
function CategoryModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    name:        initial?.name        || '',
    color:       initial?.color       || '#0085CA',
    icon:        initial?.icon        || 'meeting_room',
    description: initial?.description || '',
  });
  const [err, setErr]     = useState('');
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Editar categoría' : 'Nueva categoría'} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
        </Button>
      </>}>
      <Field label="Nombre *">
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ej: Sala de reuniones" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Color">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
              style={{ width: 36, height: 36, border: `1px solid ${BORDER}`, borderRadius: 6, cursor: 'pointer', padding: 2 }} />
            <input style={{ ...inputStyle, flex: 1 }} value={form.color} onChange={e => set('color', e.target.value)} placeholder="#0085CA" />
          </div>
        </Field>
        <Field label="Icono Material">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input style={{ ...inputStyle, flex: 1 }} value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="meeting_room" />
            <span style={{ color: form.color, flexShrink: 0 }}>
              <MIcon name={form.icon || 'meeting_room'} size={24} />
            </span>
          </div>
        </Field>
      </div>
      <Field label="Descripción">
        <input style={inputStyle} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Descripción opcional" />
      </Field>
      <ErrBanner msg={err} />
    </Modal>
  );
}

/* ── Modal de gestión de aprobadores por categoría ── */
function ApproversModal({ category, agents, onClose }) {
  const [approvers,  setApprovers]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [addingId,   setAddingId]   = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [err,        setErr]        = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/admin/approvers?category_id=${category.id}`);
      setApprovers(data);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAddAgent = async () => {
    if (!addingId) return;
    setErr('');
    try {
      await apiFetch('/admin/approvers', { method: 'POST', body: JSON.stringify({ category_id: category.id, agent_id: Number(addingId) }) });
      setAddingId('');
      load();
    } catch (e) { setErr(e.message); }
  };

  const handleAddEmail = async () => {
    const email = emailInput.trim();
    if (!email) return;
    setErr('');
    try {
      await apiFetch('/admin/approvers', { method: 'POST', body: JSON.stringify({ category_id: category.id, email }) });
      setEmailInput('');
      load();
    } catch (e) { setErr(e.message); }
  };

  const handleRemove = async (approver) => {
    setErr('');
    try {
      await apiFetch(`/admin/approvers/${approver.id}`, { method: 'DELETE' });
      load();
    } catch (e) { setErr(e.message); }
  };

  const approverAgentIds = new Set(approvers.map(a => a.agent_id).filter(Boolean));
  const available        = agents.filter(a => !approverAgentIds.has(a.id));

  return (
    <Modal title={`Aprobadores — ${category.name}`} onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>Cerrar</Button>}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>Cargando…</div>
      ) : (
        <>
          {/* Aprobadores actuales */}
          {approvers.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 16 }}>Ningún aprobador asignado a esta categoría.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {approvers.map(ap => (
                <div key={ap.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: `1px solid ${BORDER}`, borderRadius: 8, background: '#F9FAFB' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: ap.agent_id ? ACCENT : '#6B7280', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                    {ap.display_name?.[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{ap.display_name}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', gap: 6, alignItems: 'center' }}>
                      {ap.display_email}
                      {!ap.agent_id && <span style={{ background: '#F3F4F6', borderRadius: 6, padding: '1px 6px', fontSize: 10, color: '#6B7280' }}>solo email</span>}
                    </div>
                  </div>
                  <Button variant="danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => handleRemove(ap)}>
                    <MIcon name="person_remove" size={13} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Añadir por email */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 6 }}>
              Añadir por email
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} type="email"
                placeholder="usuario@uneatlantico.es"
                value={emailInput} onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddEmail()} />
              <Button variant="primary" onClick={handleAddEmail} disabled={!emailInput.trim()}>
                <MIcon name="person_add" size={14} /> Añadir
              </Button>
            </div>
          </div>

          {/* Añadir agente del sistema */}
          {available.length > 0 && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 6 }}>
                O seleccionar agente del sistema
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select style={{ ...inputStyle, flex: 1 }} value={addingId} onChange={e => setAddingId(e.target.value)}>
                  <option value="">Seleccionar agente…</option>
                  {available.map(a => <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>)}
                </select>
                <Button variant="primary" onClick={handleAddAgent} disabled={!addingId}>
                  <MIcon name="person_add" size={14} /> Añadir
                </Button>
              </div>
            </div>
          )}
          <ErrBanner msg={err} />
        </>
      )}
    </Modal>
  );
}

function TabCategories({ categories, agents, onReload }) {
  const [modal,      setModal]      = useState(null);
  const [approverCat, setApproverCat] = useState(null); // categoría cuyo modal de aprobadores está abierto

  const handleSave = async (form) => {
    if (modal === 'new') {
      await apiFetch('/admin/categories', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/admin/categories/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    onReload();
  };

  const handleDelete = async (cat) => {
    if (!confirm(`¿Eliminar la categoría "${cat.name}"? Las salas asociadas perderán su categoría.`)) return;
    try {
      await apiFetch(`/admin/categories/${cat.id}`, { method: 'DELETE' });
      onReload();
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      {modal !== null && (
        <CategoryModal
          initial={modal === 'new' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {approverCat && (
        <ApproversModal
          category={approverCat}
          agents={agents}
          onClose={() => setApproverCat(null)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button variant="primary" onClick={() => setModal('new')}>
          <MIcon name="add" size={16} /> Nueva categoría
        </Button>
      </div>

      {categories.length === 0 ? (
        <EmptyState icon="category" text="No hay categorías todavía. Crea la primera." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {categories.map(cat => (
            <div key={cat.id} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: cat.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MIcon name={cat.icon || 'meeting_room'} size={22} style={{ color: cat.color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>{cat.name}</div>
                  {cat.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{cat.description}</div>}
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
                    {cat.room_count} {cat.room_count === 1 ? 'sala' : 'salas'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'flex-end' }}>
                <Button variant="ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setApproverCat(cat)}
                  title="Gestionar aprobadores">
                  <MIcon name="manage_accounts" size={14} /> Aprobadores
                </Button>
                <Button variant="ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setModal(cat)}>
                  <MIcon name="edit" size={14} /> Editar
                </Button>
                <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDelete(cat)}>
                  <MIcon name="delete" size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 3 — UBICACIONES
══════════════════════════════════════════════════════════ */
function LocationModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    name:        initial?.name        || '',
    color:       initial?.color       || '#0085CA',
    icon:        initial?.icon        || 'location_on',
    description: initial?.description || '',
    sort_order:  initial?.sort_order  ?? 0,
  });
  const [err, setErr]     = useState('');
  const [saving, setSaving] = useState(false);
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const handleSave = async () => {
    if (!form.name.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };
  return (
    <Modal title={initial ? 'Editar ubicación' : 'Nueva ubicación'} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
        </Button>
      </>}>
      <Field label="Nombre *">
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ej: Planta -1" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Color">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
              style={{ width: 36, height: 36, border: `1px solid ${BORDER}`, borderRadius: 6, cursor: 'pointer', padding: 2 }} />
            <input style={{ ...inputStyle, flex: 1 }} value={form.color} onChange={e => set('color', e.target.value)} />
          </div>
        </Field>
        <Field label="Orden">
          <input type="number" style={inputStyle} value={form.sort_order} onChange={e => set('sort_order', Number(e.target.value))} />
        </Field>
      </div>
      <Field label="Descripción">
        <input style={inputStyle} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Descripción opcional" />
      </Field>
      <ErrBanner msg={err} />
    </Modal>
  );
}

function TabLocations({ locations, onReload }) {
  const [modal, setModal] = useState(null);

  const handleSave = async (form) => {
    if (modal === 'new') {
      await apiFetch('/admin/locations', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/admin/locations/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    onReload();
  };

  const handleDelete = async (loc) => {
    if (!confirm(`¿Eliminar la ubicación "${loc.name}"? Las salas asociadas perderán su ubicación.`)) return;
    try {
      await apiFetch(`/admin/locations/${loc.id}`, { method: 'DELETE' });
      onReload();
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      {modal !== null && (
        <LocationModal initial={modal === 'new' ? null : modal} onSave={handleSave} onClose={() => setModal(null)} />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button variant="primary" onClick={() => setModal('new')}>
          <MIcon name="add" size={16} /> Nueva ubicación
        </Button>
      </div>
      {locations.length === 0 ? (
        <EmptyState icon="location_on" text="No hay ubicaciones todavía. Crea la primera." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {locations.map(loc => (
            <div key={loc.id} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16, background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: loc.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MIcon name={loc.icon || 'location_on'} size={22} style={{ color: loc.color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>{loc.name}</div>
                  {loc.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{loc.description}</div>}
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>
                    {loc.room_count} {loc.room_count === 1 ? 'sala' : 'salas'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'flex-end' }}>
                <Button variant="ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setModal(loc)}>
                  <MIcon name="edit" size={14} /> Editar
                </Button>
                <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => handleDelete(loc)}>
                  <MIcon name="delete" size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   TAB 4 — RESERVAS
══════════════════════════════════════════════════════════ */
function RejectModal({ booking, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(reason); onClose(); }
    catch (e) { alert(e.message); setSaving(false); }
  };

  return (
    <Modal title="Rechazar reserva" onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="danger" onClick={handleConfirm} disabled={saving}>
          {saving ? 'Rechazando…' : 'Rechazar'}
        </Button>
      </>}>
      <p style={{ fontSize: 13, color: '#374151', marginTop: 0, marginBottom: 12 }}>
        Vas a rechazar la reserva de <strong>{booking.requester}</strong> para <strong>{booking.room_name}</strong>.
      </p>
      <Field label="Motivo del rechazo (opcional)">
        <textarea style={textareaStyle} value={reason} onChange={e => setReason(e.target.value)} placeholder="Indica el motivo si lo deseas…" />
      </Field>
    </Modal>
  );
}

function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function TabBookings({ bookings, filters, setFilters, onReload }) {
  const [rejectModal, setRejectModal] = useState(null);

  const handleApprove = async (booking) => {
    try {
      await apiFetch(`/admin/bookings/${booking.id}`, { method: 'PUT', body: JSON.stringify({ status: 'confirmed' }) });
      onReload();
    } catch (e) { alert(e.message); }
  };

  const handleReject = async (booking, reason) => {
    await apiFetch(`/admin/bookings/${booking.id}`, { method: 'PUT', body: JSON.stringify({ status: 'rejected', reject_reason: reason }) });
    onReload();
  };

  const handleDelete = async (booking) => {
    if (!confirm(`¿Eliminar la reserva de ${booking.requester} para ${booking.room_name}?`)) return;
    try {
      await apiFetch(`/admin/bookings/${booking.id}`, { method: 'DELETE' });
      onReload();
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      {rejectModal && (
        <RejectModal
          booking={rejectModal}
          onConfirm={(reason) => handleReject(rejectModal, reason)}
          onClose={() => setRejectModal(null)}
        />
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 4 }}>Estado</label>
          <select style={{ ...inputStyle, width: 'auto' }} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="confirmed">Confirmadas</option>
            <option value="rejected">Rechazadas</option>
            <option value="cancelled">Canceladas</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 4 }}>Desde</label>
          <input type="date" style={{ ...inputStyle, width: 'auto' }} value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 4 }}>Hasta</label>
          <input type="date" style={{ ...inputStyle, width: 'auto' }} value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        </div>
        {(filters.status !== 'all' || filters.date_from || filters.date_to) && (
          <Button variant="ghost" style={{ fontSize: 12 }}
            onClick={() => setFilters({ status: 'all', date_from: '', date_to: '' })}>
            <MIcon name="filter_alt_off" size={14} /> Limpiar filtros
          </Button>
        )}
      </div>

      {bookings.length === 0 ? (
        <EmptyState icon="event_busy" text="No hay reservas con los filtros seleccionados." />
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 780 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: `1px solid ${BORDER}` }}>
                {['Sala', 'Solicitante', 'Email', 'Inicio', 'Fin', 'Estado', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b, i) => (
                <tr key={b.id} style={{ borderBottom: i < bookings.length - 1 ? `1px solid ${BORDER}` : 'none', background: '#fff' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: '#111827' }}>{b.room_name}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{b.requester}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>
                    <a href={`mailto:${b.email}`} style={{ color: ACCENT, textDecoration: 'none' }}>{b.email}</a>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{fmtDate(b.start_time)}</td>
                  <td style={{ padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{fmtDate(b.end_time)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <StatusBadge status={b.status} />
                    {b.reject_reason && (
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={b.reject_reason}>
                        {b.reject_reason}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap' }}>
                      {b.status === 'pending' && (<>
                        <Button variant="primary" style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => handleApprove(b)}>
                          <MIcon name="check" size={13} /> Aprobar
                        </Button>
                        <Button variant="danger" style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => setRejectModal(b)}>
                          <MIcon name="close" size={13} /> Rechazar
                        </Button>
                      </>)}
                      <Button variant="ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => handleDelete(b)}
                        title="Eliminar reserva">
                        <MIcon name="delete" size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ─── Empty state ─── */
function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 16px', color: '#9CA3AF' }}>
      <MIcon name={icon} size={40} style={{ color: '#D1D5DB', marginBottom: 12, display: 'block' }} />
      <div style={{ fontSize: 14 }}>{text}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function RoomsManager() {
  const { t } = useT();
  const [tab, setTab] = useState(TAB_ROOMS);
  const [rooms,        setRooms]        = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [locations,    setLocations]    = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [agents,       setAgents]       = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [filters, setFilters] = useState({ status: 'all', date_from: '', date_to: '' });
  const pollRef = useRef(null);

  // Polling del contador de pendientes cada 30s
  const refreshPending = useCallback(async () => {
    try {
      const data = await apiFetch('/admin/pending-count');
      setPendingCount(data.count || 0);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    refreshPending();
    pollRef.current = setInterval(refreshPending, 30000);
    return () => clearInterval(pollRef.current);
  }, [refreshPending]);

  const loadData = useCallback(async (currentTab, currentFilters) => {
    setLoading(true); setError('');
    try {
      const [cats, locs, rms, ags] = await Promise.all([
        apiFetch('/admin/categories'),
        apiFetch('/admin/locations'),
        apiFetch('/admin/all'),
        fetch('/api/agents', { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
      ]);
      setCategories(cats);
      setLocations(locs);
      setRooms(rms);
      setAgents(ags);

      if (currentTab === TAB_BOOKINGS) {
        const qs = new URLSearchParams();
        if (currentFilters.status && currentFilters.status !== 'all') qs.set('status', currentFilters.status);
        if (currentFilters.date_from) qs.set('date_from', currentFilters.date_from);
        if (currentFilters.date_to) qs.set('date_to', currentFilters.date_to);
        const bks = await apiFetch(`/admin/bookings?${qs.toString()}`);
        setBookings(bks);
        refreshPending();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [refreshPending]);

  useEffect(() => { loadData(tab, filters); }, [tab, filters, loadData]);

  const reload = () => loadData(tab, filters);

  const tabs = [
    { id: TAB_ROOMS,      label: 'Salas',       icon: 'meeting_room' },
    { id: TAB_LOCATIONS,  label: 'Ubicaciones', icon: 'location_on' },
    { id: TAB_CATEGORIES, label: 'Categorías',  icon: 'category' },
    { id: TAB_BOOKINGS,   label: 'Reservas',    icon: 'event_available', badge: pendingCount },
    { id: TAB_REPORTS,    label: 'Informes',    icon: 'bar_chart' },
  ];

  return (
    <BackDeskLayout title={t('rooms.title')}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, borderBottom: `2px solid ${BORDER}`, marginBottom: 24 }}>
        {tabs.map(tk => {
          const active = tab === tk.id;
          return (
            <button key={tk.id} onClick={() => setTab(tk.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
              border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: active ? 700 : 500,
              color: active ? ACCENT : '#6B7280',
              borderBottom: `2px solid ${active ? ACCENT : 'transparent'}`,
              marginBottom: -2, transition: 'color .15s', position: 'relative',
            }}>
              <MIcon name={tk.icon} size={16} />
              {tk.label}
              {!!tk.badge && (
                <span style={{
                  background: '#EF4444', color: '#fff', borderRadius: 10,
                  fontSize: 10, fontWeight: 700, padding: '1px 6px', lineHeight: '16px',
                  minWidth: 18, textAlign: 'center',
                }}>
                  {tk.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {error && (
        <div style={{ fontSize: 13, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <MIcon name="error" size={14} style={{ marginRight: 6 }} />{error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF', fontSize: 14 }}>
          Cargando…
        </div>
      ) : (
        <>
          {tab === TAB_ROOMS      && <TabRooms      rooms={rooms} categories={categories} locations={locations} onReload={reload} />}
          {tab === TAB_CATEGORIES && <TabCategories categories={categories} agents={agents} onReload={reload} />}
          {tab === TAB_LOCATIONS  && <TabLocations  locations={locations}   onReload={reload} />}
          {tab === TAB_BOOKINGS   && <TabBookings   bookings={bookings} filters={filters} setFilters={setFilters} onReload={reload} />}
          {tab === TAB_REPORTS    && <ReportsTab rooms={rooms} categories={categories} />}
        </>
      )}
    </BackDeskLayout>
  );
}
